import { MAX_OPEN_TABS } from '../../../const/src/lib/layout.constants';
import {
  MODULOS_EN_CONSTRUCCION,
  ModuloEnConstruccion,
} from '../../../const/src/lib/modules.constant';

/** Alternativa de regex con los modulos en construccion (`mass-billing|supplies|...`). */
const EN_CONSTRUCCION = MODULOS_EN_CONSTRUCCION.join('|');

/** Modulos con pestañas en /layout/<modulo>/... */
export type LayoutModuleKey =
  | 'accounting'
  | 'tesorería'
  | 'budget'
  | 'vault'
  | 'maestros'
  | 'products'
  | 'company'
  | 'invoicing'
  | ModuloEnConstruccion;

/**
 * Decodifica percent-encoding (tesorer%C3%ADa) y unifica tesoreria/tesorería.
 * Sin esto el limite de 8 no ve las pestañas de Tesorería y el menu sí (n/8).
 */
export function normalizeLayoutRoute(route: string | null | undefined): string {
  if (!route) return '';
  let decoded = route;
  try {
    decoded = decodeURIComponent(route);
  } catch {
    decoded = route;
  }
  return decoded
    .toLowerCase()
    .replace(/tesoreria/g, 'tesorería')
    .split('?')[0]
    .split('#')[0]
    .replace(/\/+/g, '/')
    .replace(/\/$/, '');
}

/** Modulo segun el segmento /layout/<modulo>/ (criterio vault: no keywords sueltas). */
export function detectLayoutModule(route: string | null | undefined): LayoutModuleKey {
  const n = normalizeLayoutRoute(route);
  const fromPath = n.match(
    new RegExp(
      `^/layout/(accounting|tesorería|budget|vault|maestros|products|company|invoicing|${EN_CONSTRUCCION})(?:/|$)`,
    ),
  );
  if (fromPath) return fromPath[1] as LayoutModuleKey;
  if (n.includes('/budget') || n.includes('presupuesto')) return 'budget';
  if (n.includes('tesorer') || n.includes('treasury')) return 'tesorería';
  if (n.includes('/vault') || /(^|\/)vault(\/|$)/.test(n)) return 'vault';
  if (n.includes('/maestros')) return 'maestros';
  if (n.includes('/products')) return 'products';
  if (n.includes('/company')) return 'company';
  if (n.includes('/invoicing')) return 'invoicing';
  return 'accounting';
}

/** Dashboard del modulo: no cuenta para el tope de 8. */
export function isModuleBaseRoute(route: string | null | undefined, module: string): boolean {
  const n = normalizeLayoutRoute(route);
  const mod = module.replace(/tesoreria/g, 'tesorería');
  return n === `/${mod}` || n === `/layout/${mod}`;
}

/** Misma cuenta que PESTAÑAS ABIERTAS (n/8): cerrables del modulo, sin la base. */
export function countClosableTabsInModule(
  tabs: { route?: string | null }[],
  module: string,
): number {
  const mod = module.replace(/tesoreria/g, 'tesorería');
  return tabs.filter((t) => {
    if (!t.route) return false;
    if (detectLayoutModule(t.route) !== mod) return false;
    return !isModuleBaseRoute(t.route, mod);
  }).length;
}

export function isAtOpenTabLimit(
  tabs: { route?: string | null }[],
  module: string,
  max = MAX_OPEN_TABS,
): boolean {
  return countClosableTabsInModule(tabs, module) >= max;
}

/** Encuentra pestaña ya abierta aunque una ruta este encoded y la otra no. */
export function findTabByNormalizedRoute<T extends { route?: string | null; id?: string | number }>(
  tabs: T[],
  route: string | null | undefined,
): T | undefined {
  const key = normalizeLayoutRoute(route);
  if (!key) return undefined;
  return tabs.find(
    (t) =>
      normalizeLayoutRoute(t.route) === key || normalizeLayoutRoute(String(t.id ?? '')) === key,
  );
}

const LAYOUT_MODULES = `accounting|tesorería|tesoreria|budget|vault|maestros|products|company|invoicing|${EN_CONSTRUCCION}`;

/**
 * Deja la ruta lista para el router: decodifica tesorer%C3%ADa y no cambia el modulo
 * si ya viene /layout/<modulo>/. Sin decode, Tesorería se reescribe a Contabilidad
 * y el layout queda en blanco (sin pestañas).
 */
export function preserveLayoutModuleRoute(route: string, fallbackModule: string): string {
  let n = route || '';
  try {
    n = decodeURIComponent(n);
  } catch {
    /* ruta cruda */
  }
  n = n.replace(
    /\/layout\/(accounting|tesorería|tesoreria|budget|vault|maestros)\/\1\//g,
    '/layout/$1/',
  );
  n = n.replace(/\/+/g, '/');
  if (!n.startsWith('/')) n = '/' + n;
  n = n.replace(/\/layout\/tesoreria\//gi, '/layout/tesorería/');

  if (
    new RegExp(
      `^/layout/(accounting|tesorería|budget|vault|maestros|products|company|invoicing|${EN_CONSTRUCCION})/`,
    ).test(n)
  ) {
    return n;
  }

  const valid = [
    'accounting',
    'tesorería',
    'tesoreria',
    'budget',
    'vault',
    'maestros',
    'invoicing',
    ...MODULOS_EN_CONSTRUCCION,
  ];
  const target = valid.includes(fallbackModule)
    ? fallbackModule === 'tesoreria'
      ? 'tesorería'
      : fallbackModule
    : 'accounting';

  const clean = n
    .replace(new RegExp(`^/layout/(${LAYOUT_MODULES})/`), '')
    .replace(new RegExp(`^/(${LAYOUT_MODULES})/`), '')
    .replace(/^\//, '');
  return `/layout/${target}/${clean}`;
}
